import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useParams } from "react-router-dom";
import { profileService } from "../../lib/profileService";
import Card from "../ui/Card";
import Button from "../ui/Button";
import ProfilePhotoUpload from "../ui/ProfilePhotoUpload";
import CustomSelect from "../ui/CustomSelect";
import EmailVerificationModal from "../ui/EmailVerificationModal";
import { toast } from "sonner";
import {
  Settings,
  User,
  Mail,
  Phone,
  MapPin,
  Save,
  CheckCircle,
  AlertTriangle,
  Info,
  X,
  Lock,
  Shield,
} from "lucide-react";
import { getCountryName } from "../../utils/countries";
import {
  getAddressFormat,
  getStatesForCountry,
  getCitiesForCountryAndState,
  validatePostalCode,
  getPostalCodeError,
  isFieldRequired,
  getFieldLabel,
  countryHasStates,
  getCountriesForSelect,
} from "../../utils/addressFormats";

const AffiliateSettingsPage = () => {
  const { user, profile } = useAuth();
  const { affiliateName } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [profileData, setProfileData] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    fullName: "",
    phoneNumber: "",
    country: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
  });

  // Email change states
  const [newEmail, setNewEmail] = useState("");
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [pendingEmailData, setPendingEmailData] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  // Password change states
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  // Dynamic address fields state
  const [availableStates, setAvailableStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [addressFormat, setAddressFormat] = useState(
    getAddressFormat("DEFAULT")
  );
  const [fieldValidation, setFieldValidation] = useState({});

  const countries = getCountriesForSelect();

  // Load profile data on mount
  useEffect(() => {
    if (user?.id) {
      loadProfileData();
    }
  }, [user?.id]);

  const loadProfileData = async () => {
    try {
      setIsLoadingProfile(true);
      const result = await profileService.getProfileWithDetails(user.id);

      if (result.success) {
        const profile = result.profile;
        setProfileData(profile);

        // Set form data
        const formData = {
          fullName: profile.full_name || "",
          phoneNumber: profile.phone_number || "",
          country: profile.country || "",
          addressLine1: profile.address_line_1 || "",
          addressLine2: profile.address_line_2 || "",
          city: profile.city || "",
          state: profile.state || "",
          postalCode: profile.postal_code || "",
        };

        setFormData(formData);

        // Set address format and states for existing country
        if (profile.country) {
          const format = getAddressFormat(profile.country);
          setAddressFormat(format);

          const states = getStatesForCountry(profile.country);
          setAvailableStates(states);

          // Load cities if state is available
          if (profile.state) {
            const cities = getCitiesForCountryAndState(
              profile.country,
              profile.state
            );
            setAvailableCities(cities);
          }
        }

        // Set pending email data if exists
        if (profile.pending_email) {
          setPendingEmailData({
            pendingEmail: profile.pending_email,
            sentAt: profile.pending_email_sent_at,
            expiresAt: profile.pending_email_expires_at,
          });
        }

        setNewEmail(profile.email || "");
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      toast.error("Failed to load profile data");
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const handleInputChange = (field, value) => {
    console.log("Country change triggered:", field, value); // Debug log

    // Handle country change - update address format and available states
    if (field === "country") {
      const newFormat = getAddressFormat(value);
      console.log("New address format:", newFormat); // Debug log
      setAddressFormat(newFormat);

      // Load states for the selected country
      const states = getStatesForCountry(value);
      setAvailableStates(states);

      // Update form data with country and clear state/city in single call
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        state: "",
        city: "",
      }));

      setAvailableCities([]);

      // Clear postal code validation
      setFieldValidation((prev) => ({
        ...prev,
        postalCode: null,
      }));
    }

    // Handle state change - update available cities
    else if (field === "state") {
      const cities = getCitiesForCountryAndState(formData.country, value);
      setAvailableCities(cities);

      // Update form data with state and clear city in single call
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        city: "",
      }));
    }

    // Handle postal code validation
    else if (field === "postalCode") {
      const isValid = validatePostalCode(formData.country, value);
      setFieldValidation((prev) => ({
        ...prev,
        postalCode:
          value && !isValid ? getPostalCodeError(formData.country) : null,
      }));

      // Update form data
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Handle all other fields
    else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSaveProfile = async () => {
    try {
      setIsLoading(true);

      // Validate required fields based on country format
      const format = getAddressFormat(formData.country);
      const missingFields = [];

      format.required.forEach((fieldName) => {
        if (!formData[fieldName] || formData[fieldName].trim() === "") {
          missingFields.push(getFieldLabel(formData.country, fieldName));
        }
      });

      if (missingFields.length > 0) {
        toast.error(
          `Please fill in required fields: ${missingFields.join(", ")}`
        );
        setIsLoading(false);
        return;
      }

      // Validate postal code
      if (
        formData.postalCode &&
        !validatePostalCode(formData.country, formData.postalCode)
      ) {
        toast.error(getPostalCodeError(formData.country));
        setIsLoading(false);
        return;
      }

      console.log("Saving profile with data:", formData);
      console.log("User ID:", user.id);

      const result = await profileService.updateProfileInfo(user.id, formData);

      console.log("Profile update result:", result);

      if (result.success) {
        toast.success("Profile updated successfully!");
        // Reload profile data to get latest info
        await loadProfileData();
      } else {
        console.error("Profile update failed:", result);
        throw new Error(result.error || "Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error.message || "Failed to update profile");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = async () => {
    try {
      setIsChangingEmail(true);

      if (newEmail === profileData?.email) {
        toast.error("New email must be different from current email");
        return;
      }

      if (!newEmail || !newEmail.includes("@")) {
        toast.error("Please enter a valid email address");
        return;
      }

      const result = await profileService.requestEmailChange(user.id, newEmail);

      if (result.success) {
        toast.success("Verification code sent! Check your new email address.");
        setShowVerificationModal(true);
      }
    } catch (error) {
      console.error("Error requesting email change:", error);
      if (error.message.includes("already in use")) {
        toast.error("This email is already registered with another account");
      } else {
        toast.error(error.message || "Failed to request email change");
      }
    } finally {
      setIsChangingEmail(false);
    }
  };

  const handleVerifyEmailCode = async (code) => {
    try {
      setIsVerifying(true);

      const result = await profileService.verifyEmailChange(code);

      if (result.success) {
        toast.success("Email updated successfully!");
        setShowVerificationModal(false);
        setNewEmail("");
        await loadProfileData();
      }
    } catch (error) {
      console.error("Error verifying email code:", error);
      toast.error(error.message || "Invalid verification code");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCancelEmailChange = async () => {
    try {
      setIsLoading(true);

      const result = await profileService.cancelEmailChange(user.id);

      if (result.success) {
        setPendingEmailData(null);
        toast.success("Email change cancelled");
        await loadProfileData();
      }
    } catch (error) {
      console.error("Error cancelling email change:", error);
      toast.error(error.message || "Failed to cancel email change");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoUpdated = async (newPhotoUrl) => {
    // Refresh profile data when photo is updated
    await loadProfileData();
    toast.success(
      newPhotoUrl ? "Profile photo updated!" : "Profile photo removed!"
    );
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdatePassword = async () => {
    try {
      setIsUpdatingPassword(true);

      // Validate passwords
      if (!passwordData.currentPassword) {
        toast.error("Please enter your current password");
        return;
      }

      if (!passwordData.newPassword) {
        toast.error("Please enter a new password");
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        toast.error("New passwords don't match");
        return;
      }

      if (passwordData.newPassword.length < 8) {
        toast.error("Password must be at least 8 characters long");
        return;
      }

      // Update password using Supabase
      const { error } = await profileService.updatePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );

      if (error) {
        throw error;
      }

      toast.success("Password updated successfully!");

      // Reset form
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error("Error updating password:", error);
      toast.error(error.message || "Failed to update password");
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  if (isLoadingProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B8460E]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#B8460E]/10 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute bottom-0 right-0 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      <div className="relative z-10 p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <header className="mb-8">
            <h1 className="text-4xl font-bold tracking-tight text-white flex items-center gap-3">
              <Settings className="h-8 w-8 text-[#B8460E]" />
              <span>Account Settings</span>
            </h1>
            <p className="mt-2 text-lg text-gray-400">
              Manage your account preferences and personal information.
            </p>
          </header>

          {/* Main Content */}
          <div className="space-y-8">
            {/* Profile Photo Section */}
            <Card className="p-6 bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl">
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <User className="h-5 w-5 text-[#B8460E]" />
                Profile Photo
              </h3>
              <p className="text-sm text-gray-400 mb-6">
                Formats: png, jpg. Max size: 5 MB.
              </p>

              <ProfilePhotoUpload
                currentPhotoUrl={profileData?.profile_photo_url}
                onPhotoUpdated={handlePhotoUpdated}
                userId={user.id}
                size="large"
              />
            </Card>

            {/* Personal Info Section */}
            <Card className="p-6 bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl">
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <User className="h-5 w-5 text-[#B8460E]" />
                Personal Info
              </h3>
              <p className="text-sm text-gray-400 mb-6">
                Your log-in credentials and the name that is displayed in
                reports
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={formData.fullName.split(" ")[0] || ""}
                    onChange={(e) => {
                      const parts = formData.fullName.split(" ");
                      parts[0] = e.target.value;
                      handleInputChange("fullName", parts.join(" "));
                    }}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-[#B8460E]/50 focus:border-[#B8460E]/50 transition-all duration-300"
                    placeholder="Benjamin"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={
                      formData.fullName.split(" ").slice(1).join(" ") || ""
                    }
                    onChange={(e) => {
                      const firstName = formData.fullName.split(" ")[0] || "";
                      handleInputChange(
                        "fullName",
                        `${firstName} ${e.target.value}`.trim()
                      );
                    }}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-[#B8460E]/50 focus:border-[#B8460E]/50 transition-all duration-300"
                    placeholder="Randolph"
                  />
                </div>

                {/* Username (Read-only) */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Username
                  </label>
                  <input
                    type="text"
                    value={
                      profileData?.username || decodeURIComponent(affiliateName)
                    }
                    disabled
                    className="w-full px-4 py-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white/60 cursor-not-allowed"
                  />
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) =>
                      handleInputChange("phoneNumber", e.target.value)
                    }
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-[#B8460E]/50 focus:border-[#B8460E]/50 transition-all duration-300"
                    placeholder="7572920394"
                  />
                </div>

                {/* Email */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-white mb-2">
                    Email
                  </label>

                  {pendingEmailData ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-3 bg-orange-500/10 border border-orange-500/30 rounded-xl">
                        <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm text-orange-300">
                            Email change pending. Check{" "}
                            <strong>{pendingEmailData.pendingEmail}</strong> for
                            verification link.
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCancelEmailChange}
                          disabled={isLoading}
                          className="text-orange-300 hover:text-orange-200"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <input
                        type="email"
                        value={profileData?.email || ""}
                        disabled
                        className="w-full px-4 py-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white/60 cursor-not-allowed"
                      />
                    </div>
                  ) : (
                    <div className="flex gap-3">
                      <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        className="flex-1 px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-[#B8460E]/50 focus:border-[#B8460E]/50 transition-all duration-300"
                        placeholder="gray@black.com"
                      />
                      <Button
                        onClick={handleEmailChange}
                        disabled={
                          isChangingEmail || newEmail === profileData?.email
                        }
                        className="px-6 bg-white/10 hover:bg-white/20 text-white border border-white/20"
                      >
                        {isChangingEmail ? "Sending..." : "Verify to Add"}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Country
                  </label>
                  <CustomSelect
                    value={formData.country}
                    onChange={(value) => handleInputChange("country", value)}
                    options={countries}
                    placeholder="Select Country"
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white"
                  />
                </div>

                {/* Dynamic Address Fields - Reordered Layout */}
                {(() => {
                  // Define the new field order: Country and State on top row
                  const fieldOrder = [
                    "addressLine1",
                    "addressLine2",
                    "city",
                    "state",
                    "postalCode",
                  ];

                  // Reorder to: State next to Country, then shift others down
                  const reorderedFields = [];

                  // First, add state field right after country (if country has states)
                  if (countryHasStates(formData.country)) {
                    reorderedFields.push("state");
                  }

                  // Then add address1, address2, city, postalCode in that order
                  reorderedFields.push(
                    "addressLine1",
                    "addressLine2",
                    "city",
                    "postalCode"
                  );

                  return reorderedFields.map((fieldName) => {
                    // Skip if field not in current address format
                    if (!addressFormat.fields.includes(fieldName)) {
                      return null;
                    }

                    // Skip state field for countries without states
                    if (
                      fieldName === "state" &&
                      !countryHasStates(formData.country)
                    ) {
                      return null;
                    }

                    const label = getFieldLabel(formData.country, fieldName);
                    const isRequired = isFieldRequired(
                      formData.country,
                      fieldName
                    );
                    const fieldError = fieldValidation[fieldName];

                    return (
                      <div key={fieldName}>
                        <label className="block text-sm font-medium text-white mb-2">
                          {label}
                          {isRequired && (
                            <span className="text-red-400 ml-1">*</span>
                          )}
                        </label>

                        {/* State/Province Dropdown */}
                        {fieldName === "state" &&
                        countryHasStates(formData.country) &&
                        availableStates.length > 0 ? (
                          <CustomSelect
                            value={formData.state}
                            onChange={(value) =>
                              handleInputChange("state", value)
                            }
                            options={availableStates}
                            placeholder={`Select ${label}`}
                            className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white"
                          />
                        ) : (
                          /* Regular Input Field */
                          <input
                            type="text"
                            value={formData[fieldName] || ""}
                            onChange={(e) =>
                              handleInputChange(fieldName, e.target.value)
                            }
                            className={`w-full px-4 py-3 bg-white/10 backdrop-blur-sm border rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-[#B8460E]/50 focus:border-[#B8460E]/50 transition-all duration-300 ${
                              fieldError ? "border-red-500" : "border-white/20"
                            }`}
                            placeholder={
                              fieldName === "postalCode"
                                ? addressFormat.postalCodePlaceholder
                                : "Enter"
                            }
                            required={isRequired}
                          />
                        )}

                        {/* Field Error Message */}
                        {fieldError && (
                          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                            <AlertTriangle className="h-4 w-4" />
                            {fieldError}
                          </p>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Save Button */}
              <div className="mt-8 flex justify-end">
                <Button
                  onClick={handleSaveProfile}
                  disabled={isLoading}
                  className="px-8 py-3 bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white font-medium rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isLoading ? "Saving..." : "Save"}
                </Button>
              </div>
            </Card>

            {/* Password Update Section */}
            <Card className="p-6 bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl">
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <Shield className="h-5 w-5 text-[#B8460E]" />
                Update Password
              </h3>
              <p className="text-sm text-gray-400 mb-6">
                Change your password to keep your account secure
              </p>

              <div className="space-y-6">
                {/* Current Password */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      handlePasswordChange("currentPassword", e.target.value)
                    }
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-[#B8460E]/50 focus:border-[#B8460E]/50 transition-all duration-300"
                    placeholder="Enter current password"
                  />
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      handlePasswordChange("newPassword", e.target.value)
                    }
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-[#B8460E]/50 focus:border-[#B8460E]/50 transition-all duration-300"
                    placeholder="Enter new password (min 8 characters)"
                  />
                </div>

                {/* Confirm New Password */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      handlePasswordChange("confirmPassword", e.target.value)
                    }
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-[#B8460E]/50 focus:border-[#B8460E]/50 transition-all duration-300"
                    placeholder="Confirm new password"
                  />
                </div>

                {/* Update Password Button */}
                <div className="flex justify-end">
                  <Button
                    onClick={handleUpdatePassword}
                    disabled={isUpdatingPassword}
                    className="px-8 py-3 bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white font-medium rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    {isUpdatingPassword ? "Updating..." : "Update Password"}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Email Verification Modal */}
      <EmailVerificationModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        onVerify={handleVerifyEmailCode}
        newEmail={newEmail}
        isVerifying={isVerifying}
      />
    </div>
  );
};

export default AffiliateSettingsPage;
